<!--
  Specialists presentation — 2 columns ("Prezentacja Specjalistów (2 kolumny)")

  Centered header with a two-column grid of specialist cards. Each card shows a
  photo (or placeholder), name, subtitle, description preview and a "Więcej" link.
  Max width 1200px. Colours derive from `site_context`.
-->
<template>
  <section class="relative flex w-full flex-col items-center justify-center overflow-x-hidden bg-white px-4 py-12 md:px-16 lg:py-16">
    <div class="mx-auto flex w-full min-w-0 max-w-[1200px] flex-col gap-8 lg:gap-12">
      <div class="flex w-full flex-col gap-1 text-center">
        <p
          v-if="overlineText"
          class="text-[14px] font-semibold uppercase leading-5 tracking-[1px]"
        >
          {{ overlineText }}
        </p>
        <h2
          class="break-words text-[32px] font-medium leading-10 tracking-[-0.64px] lg:text-[36px] lg:leading-[44px] lg:tracking-[-0.72px]"
        >
          {{ headingText }}
        </h2>
      </div>

      <div class="grid w-full grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-12">
        <article
          v-for="specialist in specialists"
          :key="specialist.key"
          class="flex min-w-0 flex-col gap-6 sm:flex-row sm:items-start sm:gap-6"
        >
          <div
            class="relative mx-auto size-[200px] shrink-0 overflow-hidden rounded-xl bg-[#D5EEF6] sm:mx-0 sm:size-[280px]"
          >
            <img
              v-if="specialist.image"
              :src="specialist.image"
              :alt="specialist.name"
              class="block-photo absolute inset-0 size-full object-cover"
            >
            <div
              v-else
              class="absolute inset-[15%] flex items-center justify-center"
              :style="{ color: themeColor }"
            >
              <svg
                class="size-full"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="2" />
                <circle cx="24" cy="18" r="7" stroke="currentColor" stroke-width="2" />
                <path
                  d="M10 40c2.5-7 7.5-10.5 14-10.5S35.5 33 38 40"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
          </div>

          <div class="flex min-w-0 flex-1 flex-col gap-4">
            <div
              class="flex min-w-0 flex-col border-l-2 px-3"
              :style="{ borderColor: themeColor }"
            >
              <p class="break-words text-[16px] font-medium leading-6 text-neutral-900">
                {{ specialist.name }}
              </p>
              <p
                v-if="specialist.subtitle"
                class="break-words text-[14px] font-normal leading-5 text-neutral-500"
              >
                {{ specialist.subtitle }}
              </p>
            </div>

            <p
              v-if="specialist.text"
              class="line-clamp-8 whitespace-pre-wrap break-words text-[14px] font-normal leading-5 text-neutral-500"
            >
              {{ specialist.text }}
            </p>

            <a
              v-if="specialist.buttonUrl && moreButtonLabel"
              :href="specialist.buttonUrl"
              class="inline-flex h-10 w-fit items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-[14px] font-medium leading-4 transition-opacity hover:opacity-80"
              :style="{ color: buttonAccentColor }"
            >
              {{ moreButtonLabel }}
              <svg
                class="size-5 shrink-0"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M7.5 5L12.5 10L7.5 15"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </a>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { resolveSiteContext, themeColorHexFor } from '@/utils/siteThemeColors';

const props = defineProps({
  overlineText: { type: String, default: 'INSTYTUT GENOMIKI I MEDYCYNY MOLEKULARNEJ' },
  headingText: { type: String, default: 'Nasz zespół' },
  moreButtonLabel: { type: String, default: 'Więcej' },
  specialist1Name: { type: String, default: '' },
  specialist1Subtitle: { type: String, default: '' },
  specialist1Text: { type: String, default: '' },
  specialist1Image: { type: String, default: '' },
  specialist1ButtonUrl: { type: String, default: '' },
  specialist2Name: { type: String, default: '' },
  specialist2Subtitle: { type: String, default: '' },
  specialist2Text: { type: String, default: '' },
  specialist2Image: { type: String, default: '' },
  specialist2ButtonUrl: { type: String, default: '' },
  specialist3Name: { type: String, default: '' },
  specialist3Subtitle: { type: String, default: '' },
  specialist3Text: { type: String, default: '' },
  specialist3Image: { type: String, default: '' },
  specialist3ButtonUrl: { type: String, default: '' },
  specialist4Name: { type: String, default: '' },
  specialist4Subtitle: { type: String, default: '' },
  specialist4Text: { type: String, default: '' },
  specialist4Image: { type: String, default: '' },
  specialist4ButtonUrl: { type: String, default: '' },
  specialist5Name: { type: String, default: '' },
  specialist5Subtitle: { type: String, default: '' },
  specialist5Text: { type: String, default: '' },
  specialist5Image: { type: String, default: '' },
  specialist5ButtonUrl: { type: String, default: '' },
  specialist6Name: { type: String, default: '' },
  specialist6Subtitle: { type: String, default: '' },
  specialist6Text: { type: String, default: '' },
  specialist6Image: { type: String, default: '' },
  specialist6ButtonUrl: { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
});

const themeColor = computed(() => themeColorHexFor(props.site_context));

const buttonAccentColor = computed(() => {
  const site = resolveSiteContext(props.site_context);
  if (site === 'institute') return '#134e4a';
  return themeColorHexFor(props.site_context);
});

// Explicit prop references (not dynamic bracket access) so image URLs from
// DynamicBlock → block.images are reliably tracked — same pattern as AboutUs.
const specialists = computed(() => [
  {
    key: 1,
    name: props.specialist1Name,
    subtitle: props.specialist1Subtitle,
    text: props.specialist1Text,
    image: props.specialist1Image,
    buttonUrl: props.specialist1ButtonUrl,
  },
  {
    key: 2,
    name: props.specialist2Name,
    subtitle: props.specialist2Subtitle,
    text: props.specialist2Text,
    image: props.specialist2Image,
    buttonUrl: props.specialist2ButtonUrl,
  },
  {
    key: 3,
    name: props.specialist3Name,
    subtitle: props.specialist3Subtitle,
    text: props.specialist3Text,
    image: props.specialist3Image,
    buttonUrl: props.specialist3ButtonUrl,
  },
  {
    key: 4,
    name: props.specialist4Name,
    subtitle: props.specialist4Subtitle,
    text: props.specialist4Text,
    image: props.specialist4Image,
    buttonUrl: props.specialist4ButtonUrl,
  },
  {
    key: 5,
    name: props.specialist5Name,
    subtitle: props.specialist5Subtitle,
    text: props.specialist5Text,
    image: props.specialist5Image,
    buttonUrl: props.specialist5ButtonUrl,
  },
  {
    key: 6,
    name: props.specialist6Name,
    subtitle: props.specialist6Subtitle,
    text: props.specialist6Text,
    image: props.specialist6Image,
    buttonUrl: props.specialist6ButtonUrl,
  },
].filter((specialist) => specialist.name));
</script>
